/**
 * Contract endpoints — AIMS backend.
 * Source: /api/v1/aims/contracts/*
 *
 * Endpoints defined here are scaffolded for future integration.
 * Pages currently still use the Redux mock store; switching to these
 * hooks is a follow-up task.
 */

import { aimsBackendApi } from "./api"
import {
  Contract,
  ContractAttachment,
  ContractCountryOption,
  ContractAsset,
  ContractAssetCreate,
  ContractCreate,
  ContractTypeOption,
  PaginatedContractList,
  PatchedContract,
} from "./types"

export type ContractListQuery = {
  page?: number
  page_size?: number
  owner?: string[]
  subscription_year?: number[]
  country?: string[]
  contract_type?: string[]
}

export const aimsContractsApi = aimsBackendApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * GET /api/v1/aims/contracts/
     * Paginated list of contracts.
     */
    listContracts: builder.query<PaginatedContractList, ContractListQuery | void>({
      query: params => {
        const searchParams = new URLSearchParams()
        if (params?.page) searchParams.set("page", String(params.page))
        if (params?.page_size) searchParams.set("page_size", String(params.page_size))
        params?.owner?.forEach(value => searchParams.append("owner", value))
        params?.subscription_year?.forEach(value => searchParams.append("subscription_year", String(value)))
        params?.country?.forEach(value => searchParams.append("country", value))
        params?.contract_type?.forEach(value => searchParams.append("contract_type", value))

        return {
          url: "api/v1/aims/contracts/",
          params: searchParams,
        }
      },
      providesTags: result => [
        { type: "AimsContractList" as const, id: "LIST" },
        ...(result?.results || []).map(c => ({ type: "AimsContract" as const, id: c.id })),
      ],
    }),

    /**
     * GET /api/v1/aims/contracts/{id}/
     * Retrieve a single contract by ID.
     */
    getContract: builder.query<Contract, number>({
      query: id => ({
        url: `api/v1/aims/contracts/${id}/`,
      }),
      providesTags: (_r, _e, id) => [{ type: "AimsContract" as const, id }],
    }),

    /**
     * POST /api/v1/aims/contracts/
     * Create a new contract.
     */
    createContract: builder.mutation<Contract, ContractCreate>({
      query: body => ({
        url: "api/v1/aims/contracts/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AimsContractList" as const, id: "LIST" }],
    }),

    /**
     * PUT /api/v1/aims/contracts/{id}/
     * Full replacement update (all fields required).
     */
    updateContract: builder.mutation<Contract, { id: number; body: ContractCreate }>({
      query: ({ id, body }) => ({
        url: `api/v1/aims/contracts/${id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsContract" as const, id },
        { type: "AimsContractList" as const, id: "LIST" },
      ],
    }),

    /**
     * PATCH /api/v1/aims/contracts/{id}/
     * Partial update (only changed fields).
     */
    patchContract: builder.mutation<Contract, { id: number; body: PatchedContract }>({
      query: ({ id, body }) => ({
        url: `api/v1/aims/contracts/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsContract" as const, id },
        { type: "AimsContractList" as const, id: "LIST" },
      ],
    }),

    /**
     * DELETE /api/v1/aims/contracts/{id}/
     */
    deleteContract: builder.mutation<void, number>({
      query: id => ({
        url: `api/v1/aims/contracts/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AimsContract" as const, id },
        { type: "AimsContractList" as const, id: "LIST" },
      ],
    }),

    /**
     * GET /api/v1/aims/contracts/types/
     */
    listContractTypes: builder.query<ContractTypeOption[], void>({
      query: () => ({
        url: "api/v1/aims/contracts/types/",
      }),
      providesTags: [{ type: "AimsContractFilterOptions" as const, id: "LIST" }],
    }),

    /**
     * GET /api/v1/aims/contracts/countries/
     */
    listContractCountries: builder.query<ContractCountryOption[], void>({
      query: () => ({
        url: "api/v1/aims/contracts/countries/",
      }),
      providesTags: [{ type: "AimsContractFilterOptions" as const, id: "LIST" }],
    }),

    /**
     * GET /api/v1/aims/contracts/owners/
     */
    listContractOwners: builder.query<string[], void>({
      query: () => ({
        url: "api/v1/aims/contracts/owners/",
      }),
      providesTags: [{ type: "AimsContractFilterOptions" as const, id: "LIST" }],
    }),

    /**
     * GET /api/v1/aims/contracts/subscription-years/
     */
    listContractSubscriptionYears: builder.query<number[], void>({
      query: () => ({
        url: "api/v1/aims/contracts/subscription-years/",
      }),
      providesTags: [{ type: "AimsContractFilterOptions" as const, id: "LIST" }],
    }),

    /**
     * POST /api/v1/aims/contract-assets/
     * Attach an asset to a contract for a specific contract_type.
     * The same (contract, asset) pair may be attached more than once
     * for different types (e.g. once for ADA, once for LIA).
     */
    createContractAsset: builder.mutation<ContractAsset, ContractAssetCreate>({
      query: body => ({
        url: "api/v1/aims/contract-assets/",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, body) => [
        { type: "AimsContract" as const, id: body.contract },
        { type: "AimsContractList" as const, id: "LIST" },
        { type: "AimsRoundList" as const, id: "LIST" },
      ],
    }),

    listContractAttachments: builder.query<ContractAttachment[], number>({
      query: contractId => ({ url: `api/v1/aims/contracts/${contractId}/attachments/` }),
      providesTags: result => [
        { type: "AimsContractAttachment" as const, id: "LIST" },
        ...(result ?? []).map(attachment => ({ type: "AimsContractAttachment" as const, id: attachment.id })),
      ],
    }),

    uploadContractAttachment: builder.mutation<ContractAttachment, { contractId: number; file: File }>({
      query: ({ contractId, file }) => {
        const body = new FormData()
        body.append("file", file)
        return {
          url: `api/v1/aims/contracts/${contractId}/attachments/`,
          method: "POST",
          body,
        }
      },
      invalidatesTags: [{ type: "AimsContractAttachment" as const, id: "LIST" }],
    }),

    downloadContractAttachment: builder.mutation<Blob, number>({
      query: id => ({
        url: `api/v1/aims/contract-attachments/${id}/download/`,
        responseHandler: response => response.blob(),
      }),
    }),

    deleteContractAttachment: builder.mutation<void, number>({
      query: id => ({
        url: `api/v1/aims/contract-attachments/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AimsContractAttachment" as const, id: "LIST" }],
    }),
  }),
})

export const {
  useListContractsQuery,
  useGetContractQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  usePatchContractMutation,
  useDeleteContractMutation,
  useListContractTypesQuery,
  useListContractCountriesQuery,
  useListContractOwnersQuery,
  useListContractSubscriptionYearsQuery,
  useCreateContractAssetMutation,
  useListContractAttachmentsQuery,
  useUploadContractAttachmentMutation,
  useDownloadContractAttachmentMutation,
  useDeleteContractAttachmentMutation,
} = aimsContractsApi
